import type {ChromelessPlayer, TypedSource} from 'theoplayer';

import {THEOplayerAnalyticsStateMachine} from '../../analyticsStateMachines/THEOplayerAnalyticsStateMachine';
import type {Analytics} from '../../core/Analytics';
import {HeartbeatService} from '../../core/HeartbeatService';
import {CastTech} from '../../enums/CastTech';
import {Event} from '../../enums/Event';
import {Player as PlayerEnum} from '../../enums/Player';
import {PlayerSize} from '../../enums/PlayerSize';
import {getStreamTypeFromMIMEType, getStreamTypesFromURL, StreamTypes} from '../../enums/StreamTypes';
import {ErrorDetailBackend} from '../../features/errordetails/ErrorDetailBackend';
import {ErrorDetailTracking} from '../../features/errordetails/ErrorDetailTracking';
import type {Feature} from '../../features/Feature';
import type {FeatureConfig} from '../../features/FeatureConfig';
import type {AnalyticsStateMachineOptions} from '../../types/AnalyticsStateMachineOptions';
import type {QualityChangeEvent} from '../../types/EventData';
import type {FeatureConfigContainer} from '../../types/FeatureConfigContainer';
import {buildPlaybackInfoStreamTypeFields, normalizeVideoDuration, PlaybackInfo} from '../../types/PlaybackInfo';
import {isBlank, isValidString} from '../../utils/stringUtils';
import {isNotNullish, isNullish, tryJSONStringify} from '../../utils/Utils';
import {getWindowTheoPlayerVersion} from '../../utils/windowUtils';
import {InternalAdapter} from '../internal/InternalAdapter';
import type {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

export class THEOplayerInternalAdapter extends InternalAdapter implements InternalAdapterAPI {
  private readonly player: ChromelessPlayer;

  /** Boolean flag to prevent multiple calls of the {@link Event.UNLOAD} event */
  private onBeforeUnLoadEvent = false;

  private lastTrackedCurrentTime: number | undefined;

  private currentSource: TypedSource | undefined;

  constructor(player: any, opts?: AnalyticsStateMachineOptions) {
    super(opts);
    const playingHeartbeatService = new HeartbeatService(() => this.currentTime);
    this.stateMachine = new THEOplayerAnalyticsStateMachine(
      this.stateMachineCallbacks,
      playingHeartbeatService,
      this.opts,
    );
    playingHeartbeatService.setListener(this.stateMachine);

    this.player = player;
  }

  initialize(analytics: Analytics): Array<Feature<FeatureConfigContainer, FeatureConfig>> {
    this.registerEventListeners();

    this.ssaiService.activate({
      getPlayerCurrentTime: () => this.currentTime,
      reportEngagementInteraction: (ssaiEngagementInteractionData) => {
        analytics.sendSsaiEngagementAdSample(ssaiEngagementInteractionData);
      },
      reportPlaybackInteraction: (timestamp: number, playerCurrentTime: number) => {
        // Because ssai playback interaction can happen before Ssai Service is active (e.g. first ssai startAd call),
        // we need to manually mark sample as triggered by ssai
        //
        // This is also including the last sample marking, before a ssai ad block (due to ssai being spiky by design)
        this.stateMachine.sendPlayingSample(timestamp, playerCurrentTime, true);
      },
    });

    const errorDetailTracking = new ErrorDetailTracking(
      analytics.errorDetailTrackingSettingsProvider,
      new ErrorDetailBackend(analytics.errorDetailTrackingSettingsProvider.collectorConfig),
      [analytics.errorDetailSubscribable],
      undefined,
    );

    this.eventCallback(Event.READY, {currentTime: this.currentTime});

    return [errorDetailTracking];
  }

  getPlayerVersion(): string {
    // try to get the version from the global `window.THEOplayer` (if npm module is used, it doesn't exist)
    const windowTHEOplayerVersion = getWindowTheoPlayerVersion();
    if (isValidString(windowTHEOplayerVersion) && !isBlank(windowTHEOplayerVersion)) {
      return windowTHEOplayerVersion;
    }

    return 'unknown';
  }
  getPlayerName(): string {
    return PlayerEnum.THEOPLAYER;
  }
  getPlayerTech(): string {
    return 'html5';
  }
  getAutoPlay(): boolean {
    return this.player.autoplay ?? false;
  }

  getCurrentPlaybackInfo(): PlaybackInfo {
    const activeVideoTrack = this.player.videoTracks.find((t) => t.enabled);
    const activeAudioTrack = this.player.audioTracks.find((t) => t.enabled);
    const activeTextTrack = this.player.textTracks.find((t) => t.mode === 'showing');

    return {
      ...this.getCommonPlaybackInfo(),
      ...detectPlaybackInfoStreamTypeFieldsFromSource(this.currentSource),
      audioBitrate: activeAudioTrack?.activeQuality?.bandwidth,
      audioCodec: activeAudioTrack?.activeQuality?.codecs,
      audioLanguage: activeAudioTrack?.language,
      castTech:
        this.player.cast?.airplay != null
          ? CastTech.AirPlay
          : this.player.cast?.chromecast != null
            ? CastTech.GoogleCast
            : undefined,
      droppedFrames: 0,
      isCasting: this.player.cast?.casting === true,
      isLive: !isFinite(this.player.duration),
      isMuted: this.player.muted,
      playerTech: this.getPlayerTech(),
      size: this.player.presentation.currentMode === 'fullscreen' ? PlayerSize.Fullscreen : PlayerSize.Window,
      subtitleEnabled: activeTextTrack != null,
      subtitleLanguage: activeTextTrack?.language,
      videoBitrate: activeVideoTrack?.activeQuality?.bandwidth,
      videoCodec: activeVideoTrack?.activeQuality?.codecs,
      videoDuration: normalizeVideoDuration(this.player.duration),
      videoPlaybackHeight: this.player.videoHeight,
      videoPlaybackWidth: this.player.videoWidth,
      videoWindowHeight: this.player.element.offsetHeight,
      videoWindowWidth: this.player.element.offsetWidth,
    } as PlaybackInfo;
  }

  protected override get currentTime(): number {
    return this.player.currentTime;
  }

  override resetSourceRelatedState(): void {
    super.resetSourceRelatedState();
    this.ssaiService.resetSourceRelatedState();
    this.lastTrackedCurrentTime = undefined;
    this.currentSource = undefined;
  }

  private registerEventListeners() {
    this.player.addEventListener('sourcechange', (_event) => {
      // We use the 'sourcechange' event to indicate that the player that was in State.SOURCE_CHANGING is ready
      // with a new source and should move to State.READY. It doesn't mean that the source is fully loaded, it
      // only means that the player is ready to play (play button is enabled), and a video source can be loaded
      // later as part of the startup.
      //
      // Why not use the 'loadedmetadata' event?
      //  Because we need to support also player setup without preload (preload="none"), where 'loadedmetadata'
      //  is not fired, but the player is already able to trigger "play" action (play button or API call is available)
      //  aka. the player should already leave State.SOURCE_CHANGING and be in State.READY, because if a "play" action
      //  is triggered, we should transition to State.STARTUP. Without preload, 'loadedmetadata' is fired later, and
      //  we track a whole source loading time as part of startup.
      this.eventCallback(Event.READY, {});
    });

    this.player.addEventListener('currentsourcechange', (event) => {
      this.currentSource = event.currentSource;
    });

    this.player.addEventListener('play', (event) => {
      this.eventCallback(Event.PLAY, {currentTime: event.currentTime});
    });

    this.player.addEventListener('pause', (event) => {
      this.eventCallback(Event.PAUSE, {currentTime: event.currentTime});
    });

    this.player.addEventListener('representationchange', (event) => {
      const previousRepresentation = event.previousRepresentation;
      const representation = event.representation;
      if (isNullish(representation)) {
        return;
      }

      if (representation.type === 'video') {
        const qualityEventInfo: QualityChangeEvent = {
          type: 'video',
          width: representation.width,
          height: representation.height,
          bitrate: representation.bandwidth,
          currentTime: this.currentTime,
        };

        // for initial quality changes (no previous representation set) we do not want to send a qualitychange sample
        if (
          isNotNullish(previousRepresentation) &&
          this.qualityChangeService.shouldAllowVideoQualityChange(representation.bandwidth)
        ) {
          this.eventCallback(Event.VIDEO_CHANGE, qualityEventInfo);
        }

        this.qualityChangeService.setVideoBitrate(representation.bandwidth);
        this.stateMachineCallbacks.updateSampleQualityInfo(qualityEventInfo);
      } else if (representation.type === 'audio') {
        const qualityEventInfo: QualityChangeEvent = {
          type: 'audio',
          bitrate: representation.bandwidth,
          currentTime: this.currentTime,
        };

        // for initial quality changes (no previous representation set) we do not want to send a qualitychange sample
        if (
          isNotNullish(previousRepresentation) &&
          this.qualityChangeService.shouldAllowAudioQualityChange(representation.bandwidth)
        ) {
          this.eventCallback(Event.AUDIO_CHANGE, qualityEventInfo);
        }

        this.qualityChangeService.setAudioBitrate(representation.bandwidth);
        this.stateMachineCallbacks.updateSampleQualityInfo(qualityEventInfo);
      }
    });

    this.player.addEventListener('seeking', (_event) => {
      // we have to send the last tracked time as currentTime here,
      // because at this point the event.currentTime is already set to the new seeked time
      this.eventCallback(Event.SEEK, {currentTime: this.lastTrackedCurrentTime});
    });

    this.player.addEventListener('seeked', (event) => {
      this.eventCallback(Event.SEEKED, {currentTime: event.currentTime});
    });

    this.player.addEventListener('timeupdate', (event) => {
      this.lastTrackedCurrentTime = event.currentTime;
    });

    this.player.addEventListener('playing', (event) => {
      this.eventCallback(Event.PLAYING, {currentTime: event.currentTime});
    });

    this.player.addEventListener('waiting', (event) => {
      this.eventCallback(Event.START_BUFFERING, {currentTime: event.currentTime});
    });

    this.player.addEventListener('emptied', (_event) => {
      // From the observation of player behavior, we saw that the 'emptied' event is triggered always when
      // a source is changed, and not during normal startup, so it helps us to better distinguish manual source
      // changes from the initial load.
      this.eventCallback(Event.MANUAL_SOURCE_CHANGE, {currentTime: this.lastTrackedCurrentTime, config: {}});
    });

    this.player.addEventListener('segmenterror', (event) => {
      // theoplayer does not provide a correct type for the segment error event,
      // so we have to use any here to get `error` property and its data
      const code = isNotNullish(event['error']?.code) ? Number(event['error']?.code) : undefined;
      const maybeMessage = event['error']?.message;
      const message = isValidString(maybeMessage) ? maybeMessage : undefined;
      const originalError = event['error']?.cause;
      const maybeStacktrace = event['error']?.stack;
      const stacktrace = isValidString(maybeStacktrace) ? maybeStacktrace.split('\n') : undefined;
      const errorAsData = tryJSONStringify(event);

      this.eventCallback(Event.ERROR, {
        code: code,
        message: message,
        legacyData: errorAsData,
        currentTime: this.currentTime,
        originalError: originalError,
        data: {
          exceptionMessage: message,
          exceptionStacktrace: stacktrace,
          additionalData: errorAsData,
        },
      });

      this.ssaiService.sendAdEngagementErrorSample({code: code, message: message, severity: 'CRITICAL'}, event);
    });

    this.player.addEventListener('error', (event) => {
      const code = event.errorObject.code;
      const message = event.errorObject.message;
      const originalError = event.errorObject.cause;
      const errorAsData = tryJSONStringify(event.errorObject);

      this.eventCallback(Event.ERROR, {
        code: code,
        message: message,
        legacyData: errorAsData,
        currentTime: this.currentTime,
        originalError: originalError,
        data: {
          exceptionMessage: message,
          exceptionStacktrace: event.errorObject.stack?.split('\n') ?? undefined,
          additionalData: errorAsData,
        },
      });

      this.ssaiService.sendAdEngagementErrorSample({code: code, message: message, severity: 'CRITICAL'}, event);
    });

    const handleCollectorUnload = () => {
      if (!this.onBeforeUnLoadEvent) {
        this.onBeforeUnLoadEvent = true;
        // we use here the last tracked video time as we cannot be sure if the player is still working at this place
        const currentTime = this.lastTrackedCurrentTime;
        this.eventCallback(Event.UNLOAD, {currentTime});
      }
      this.release();
    };

    this.player.addEventListener('destroy', (_event) => {
      handleCollectorUnload();
    });
    this.windowEventTracker.addEventListener('beforeunload', handleCollectorUnload);
    this.windowEventTracker.addEventListener('unload', handleCollectorUnload);
  }
}

export function detectPlaybackInfoStreamTypeFieldsFromSource(
  source: TypedSource | undefined,
): Pick<PlaybackInfo, 'm3u8Url' | 'mpdUrl' | 'progUrl' | 'streamFormat'> {
  if (isNullish(source)) {
    return {};
  }

  let streamType: StreamTypes | undefined = undefined;
  if (!isNullish(source.type)) {
    streamType = getStreamTypeFromMIMEType(source.type);
  }
  if (isNullish(streamType)) {
    streamType = getStreamTypesFromURL(source.src);
  }

  return buildPlaybackInfoStreamTypeFields(streamType, source.src);
}
